import {Copy, Sparkles} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import CategoryPicker from 'src/CategoryPicker';
import InputGroup from 'src/InputGroup';
import PayeeInput from 'src/inputs/PayeeInput';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import type {Option} from '@danwang/option';
import type {CategorySuggestion} from 'src/CategoryPicker';
import type {PayeeSuggestion} from 'src/inputs/PayeeInput';

export type PaymentTransactionFieldsValue = {
  payee: string;
  category: Option<string>;
  description: string;
};

// A historical categorization match for the selection being categorized —
// structurally compatible with the CategorizationSuggestion GraphQL type.
// Ordered most-frequent first by the caller.
export type FieldSuggestion = {
  payee: string;
  categoryName: string;
  matchCount: number;
  totalCount: number;
};

// The values auto-applied from the dominant suggestion. Provenance badges
// render only while the field still holds its auto-applied value; editing a
// field clears its badge without touching the other.
export type AutoAppliedValues = {
  payee?: string;
  category?: string;
};

export type CopyableField =
  | 'payee'
  | 'category'
  | 'description'
  | 'tagsAndFields';

type Props = {
  value: PaymentTransactionFieldsValue;
  onChange: (value: PaymentTransactionFieldsValue) => void;
  // When provided, each field gets a "copy to all" button that propagates its
  // value to sibling forms (e.g. the other splits in the split modal).
  onCopyToAll?: (field: CopyableField) => void;
  // Historical matches: pinned to the top of the payee typeahead and the
  // category picker, and the source of provenance badges for auto-applied
  // values.
  suggestions?: FieldSuggestion[];
  autoApplied?: AutoAppliedValues | null;
  autoFocusPayee?: boolean;
  // Fired on the user's first keystroke in a field — before its picker
  // commits the text into `value` — so the caller can stop a late
  // suggestion response from overwriting in-progress input.
  onFieldEdited?: (field: 'payee' | 'category') => void;
};

type CopyToAllButtonProps = {
  onClick: () => void;
};

export const CopyToAllButton = ({onClick}: CopyToAllButtonProps) => (
  <Button
    variant="ghost"
    size="icon"
    type="button"
    onClick={onClick}
    title="Copy to all splits"
    aria-label="Copy to all splits"
    className="shrink-0 p-1.5"
  >
    <Copy className="size-4" />
  </Button>
);

const suggestionDetail = (s: FieldSuggestion): string =>
  `${s.matchCount} of ${s.totalCount}`;

// Sparkle + provenance rendered inside an input holding an auto-applied value.
const AppliedBadge = ({detail}: {detail: string | null}) => (
  <span className="text-muted-foreground flex items-center gap-1 text-xs">
    <Sparkles className="text-foreground size-3" />
    {detail}
  </span>
);

export const PaymentTransactionFields = ({
  value,
  onChange,
  onCopyToAll,
  suggestions,
  autoApplied,
  autoFocusPayee,
  onFieldEdited,
}: Props) => {
  const withCopyToAll = (field: CopyableField, input: React.ReactNode) =>
    onCopyToAll === undefined ? (
      input
    ) : (
      <div className="flex items-center gap-1">
        <div className="flex-1">{input}</div>
        <CopyToAllButton onClick={() => onCopyToAll(field)} />
      </div>
    );

  // Distinct payees/categories in caller order (most-frequent first), each
  // carrying the provenance of its best match.
  const payeeSuggestions = React.useMemo((): PayeeSuggestion[] => {
    const seen = new Set<string>();
    const out: PayeeSuggestion[] = [];
    for (const s of suggestions ?? []) {
      if (s.payee !== '' && !seen.has(s.payee)) {
        seen.add(s.payee);
        out.push({name: s.payee, detail: suggestionDetail(s)});
      }
    }
    return out;
  }, [suggestions]);

  const categorySuggestions = React.useMemo((): CategorySuggestion[] => {
    const seen = new Set<string>();
    const out: CategorySuggestion[] = [];
    for (const s of suggestions ?? []) {
      if (s.categoryName !== '' && !seen.has(s.categoryName)) {
        seen.add(s.categoryName);
        out.push({name: s.categoryName, detail: suggestionDetail(s)});
      }
    }
    return out;
  }, [suggestions]);

  const payeeBadgeDetail =
    autoApplied?.payee != null && value.payee === autoApplied.payee
      ? (payeeSuggestions.find((s) => s.name === value.payee)?.detail ?? null)
      : undefined;
  const currentCategory = value.category.getOrElse('');
  const categoryBadgeDetail =
    autoApplied?.category != null && currentCategory === autoApplied.category
      ? (categorySuggestions.find((s) => s.name === currentCategory)?.detail ??
        null)
      : undefined;

  // Brief green wash when values land via auto-apply, so the form visibly
  // "did something" rather than silently arriving pre-filled. One-shot:
  // `autoApplied` also changes identity when an edit clears a field's
  // provenance, and that must not flash the other field again.
  const [washed, setWashed] = React.useState(false);
  const washedOnceRef = React.useRef(false);
  React.useEffect(() => {
    if (autoApplied == null || washedOnceRef.current) return;
    washedOnceRef.current = true;
    setWashed(true);
    const timeout = setTimeout(() => setWashed(false), 1200);
    return () => clearTimeout(timeout);
  }, [autoApplied]);
  const washClassName = (applied: boolean) =>
    cn(
      'transition-colors duration-700',
      washed && applied && 'bg-green-500/10 dark:bg-green-400/10',
    );

  return (
    <>
      <InputGroup label="Payee">
        {withCopyToAll(
          'payee',
          <PayeeInput
            value={value.payee}
            onChange={(payee) => onChange({...value, payee})}
            autoFocus={autoFocusPayee}
            onEdited={
              onFieldEdited ? () => onFieldEdited('payee') : undefined
            }
            suggestions={payeeSuggestions}
            adornment={
              payeeBadgeDetail !== undefined ? (
                <AppliedBadge detail={payeeBadgeDetail} />
              ) : undefined
            }
            inputClassName={washClassName(autoApplied?.payee != null)}
          />,
        )}
      </InputGroup>
      <InputGroup label="Category">
        {withCopyToAll(
          'category',
          <CategoryPicker
            selectedCategory={value.category}
            onSelectCategory={(category) => onChange({...value, category})}
            onEdited={
              onFieldEdited ? () => onFieldEdited('category') : undefined
            }
            suggestions={categorySuggestions}
            adornment={
              categoryBadgeDetail !== undefined ? (
                <AppliedBadge detail={categoryBadgeDetail} />
              ) : undefined
            }
            inputClassName={washClassName(autoApplied?.category != null)}
          />,
        )}
      </InputGroup>
      <InputGroup label="Description">
        {withCopyToAll(
          'description',
          <TextInput
            value={value.description}
            onChange={(description) => onChange({...value, description})}
            placeholder="Add description"
          />,
        )}
      </InputGroup>
    </>
  );
};
